import { Link } from 'react-router-dom'
import Logo from '@/components/shared/Logo'

const services = [
  { name: 'Web App', status: 'operational' },
  { name: 'API', status: 'operational' },
  { name: 'Authentication', status: 'operational' },
  { name: 'File Storage', status: 'operational' },
  { name: 'Notifications', status: 'operational' },
]

type Incident = {
  date: string
  title: string
  impact?: 'minor' | 'major' | 'info' | string
  resolved?: boolean
  details?: string
}

const incidents: Incident[] = [
  // Example structure. Keep empty for "no incidents".
  // { date: '2025-12-10', title: 'Brief API latency', impact: 'minor', resolved: true, details: 'Autoscaling event restored normal latency in 8 minutes.' },
]

export default function Status() {
  const allOperational = services.every((s) => s.status === 'operational')

  const pill = (s: string) =>
    s === 'operational'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      : s === 'degraded'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'

  const label = (s: string) =>
    s === 'operational' ? 'Operational' : s === 'degraded' ? 'Degraded' : 'Down'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>

            <nav aria-label="Breadcrumb" className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li><Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">Home</Link></li>
                <li aria-hidden>•</li>
                <li className="text-gray-700 dark:text-gray-200 font-medium">System Status</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
        {/* Status banner */}
        <div className={`rounded-xl border p-5 mb-8 shadow-sm ${allOperational ? 'border-green-200 bg-green-50 dark:border-green-900/50 dark:bg-green-950/30' : 'border-yellow-200 bg-yellow-50 dark:border-yellow-900/50 dark:bg-yellow-950/30'}`}>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${allOperational ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>
              {allOperational ? 'All Systems Operational' : 'Partial Degradation'}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">Updated just now</span>
          </div>
          <p className="text-gray-800 dark:text-gray-200 font-medium mt-2">
            Real-time service health for AsiliFlow infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Service health */}
          <section className="lg:col-span-7">
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Service Health</h2>
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {services.map((s) => (
                  <li key={s.name} className="py-4 flex items-center justify-between">
                    <span className="text-gray-800 dark:text-gray-200">{s.name}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pill(s.status)}`}>
                      {label(s.status)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Incidents & maintenance */}
          <section className="lg:col-span-5">
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Past Incidents</h2>
              {incidents.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">No incidents reported in the past 30 days.</p>
              ) : (
                <ul className="space-y-4">
                  {incidents.map((i, idx) => (
                    <li key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-gray-900 dark:text-gray-100 font-medium">{i.title}</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          i.impact === 'minor'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : i.impact === 'major'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {i.impact?.toString().toUpperCase() || 'INFO'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{i.date} • {i.resolved ? 'Resolved' : 'Investigating'}</p>
                      <p className="text-gray-700 dark:text-gray-300 mt-2">{i.details}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm mt-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Scheduled Maintenance</h2>
              <p className="text-gray-500 dark:text-gray-400">No maintenance scheduled at this time.</p>
            </div>
          </section>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
          >
            Report an Issue
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
